import { formatRelativeDate } from '@littlebird/ui-utils';

import type { WikiLittlebirdAccount, WikiPerson } from '../people-wiki.types';

/** What the user can do with one address of a person, which is also the face of its button:
 * - `accept`: the address asked to connect
 * - `connect`: the address has a Littlebird account and no connection yet
 * - `requested`: the user asked, and waits for an answer
 * - `connected`
 * - `invite`: the address has no Littlebird account
 * - `invited`: the user invited the address, and the wait before inviting again is running
 * - `inviteAgain`: the user invited the address, and the wait is over */
export type AddressState =
  | 'accept'
  | 'connect'
  | 'requested'
  | 'connected'
  | 'invite'
  | 'invited'
  | 'inviteAgain';

/** One address of a person, with its Littlebird account when it has one, and what the user can do with it */
export type AddressEntry = {
  email: string;
  account: WikiLittlebirdAccount | null;
  state: AddressState;
};

export const STATE_LABELS: Record<AddressState, string> = {
  accept: 'Accept',
  connect: 'Connect',
  requested: 'Requested',
  connected: 'Connected',
  invite: 'Invite',
  invited: 'Invited',
  inviteAgain: 'Invite again',
};

/** What a click on an address sends: an invite when the address has no Littlebird account, and a request otherwise */
export const sendingOf = (state: AddressState): 'invite' | 'request' =>
  state === 'invite' || state === 'inviteAgain' ? 'invite' : 'request';

const DAY_MS = 24 * 60 * 60_000;

/** Whole days until `iso`, and at least one while it is still ahead, so a wait never reads as 0 days */
export const daysUntil = (iso: string, now = Date.now()): number =>
  Math.max(1, Math.ceil((Date.parse(iso) - now) / DAY_MS));

export const formatDays = (days: number): string =>
  `${days} ${days === 1 ? 'day' : 'days'}`;

/** The days until the user can invite or ask the address of this account again, or null when there is no wait */
export const inviteWaitDays = (
  { resendAt }: WikiLittlebirdAccount,
  now = Date.now()
): number | null =>
  resendAt !== null && Date.parse(resendAt) > now
    ? daysUntil(resendAt, now)
    : null;

/** When a request or an invite was sent, to follow a dot, where "Yesterday" and "Just now" read better in lower case. A
 * date such as "Oct 3" keeps its capital. */
export const sentLabel = (sentAt: string): string => {
  const label = formatRelativeDate(sentAt);
  return label === 'Yesterday' || label === 'Just now'
    ? label.toLowerCase()
    : label;
};

/** What the actions depend on: the addresses of a person and the Littlebird accounts on the page */
export type ActionSubject = Pick<
  WikiPerson,
  | 'isSelf'
  | 'isTeammate'
  | 'primaryEmail'
  | 'otherEmails'
  | 'littlebirdAccounts'
>;

/** An address that the user typed, which no page holds yet, as the subject of its actions: all it can be offered is
 * Connect or Invite */
export const addressSubject = (email: string): ActionSubject => ({
  isSelf: false,
  isTeammate: false,
  primaryEmail: email,
  otherEmails: [],
  littlebirdAccounts: [],
});

/** The person's addresses, the primary one first */
const personAddresses = (person: ActionSubject): string[] =>
  [person.primaryEmail, ...person.otherEmails].filter(
    (email) => email !== null
  );

/** The addresses of the person whose Littlebird account the page does not tell, which the server is asked about. Each
 * address looked up counts against a daily limit, so the user's own page and the addresses that hold a connection or
 * belong to a teammate are left out. */
export const addressesToLookUp = (person: ActionSubject): string[] => {
  if (person.isSelf) return [];
  const addressesWithConnectionOrTeam = person.littlebirdAccounts
    .filter(({ connection, isTeammate }) => connection !== '' || isTeammate)
    .map(({ email }) => email);
  return personAddresses(person).filter(
    (email) => !addressesWithConnectionOrTeam.includes(email)
  );
};

// The state that matters most leads. A request to answer comes first, then a Connect to send, then what is under way
// or held, and last the invites.
const IMPORTANCE: readonly AddressState[] = [
  'accept',
  'connect',
  'requested',
  'connected',
  'invite',
  'inviteAgain',
  'invited',
];

const importanceOf = ({ state }: AddressEntry): number =>
  IMPORTANCE.indexOf(state);

const canInviteAgain = (account: WikiLittlebirdAccount, now: number): boolean =>
  inviteWaitDays(account, now) === null;

type Context = {
  /** The addresses that have a Littlebird account, or null when the lookup has not answered or could not */
  littlebirdEmails: string[] | null;
  isTeammate: boolean;
  now: number;
};

const stateOf = (
  email: string,
  account: WikiLittlebirdAccount | null,
  { littlebirdEmails, isTeammate, now }: Context
): AddressState | null => {
  if (account?.isTeammate) return null;
  switch (account?.connection) {
    case 'incoming':
      return 'accept';
    case 'connected':
      return 'connected';
    case 'requested':
      return 'requested';
    case 'invited':
      return canInviteAgain(account, now) ? 'inviteAgain' : 'invited';
    default:
      // An address that the lookup could not check is neither a Connect nor an Invite, and a teammate needs neither
      if (littlebirdEmails === null || isTeammate) return null;
      return littlebirdEmails.includes(email) ? 'connect' : 'invite';
  }
};

/** What the user can do with each address of the person, the most important state first. Each address decides on its
 * own: one on Littlebird is a Connect and one that is not is an Invite, whatever the other addresses are. A teammate and
 * the user's own page have no actions. `littlebirdEmails` are the person's addresses that have a Littlebird account, or
 * null while the lookup has not answered and when it could not: then no address gets a Connect or an Invite. */
export const connectionActions = (
  person: ActionSubject,
  littlebirdEmails: string[] | null,
  now = Date.now()
): AddressEntry[] => {
  if (person.isSelf) return [];
  const context = { littlebirdEmails, isTeammate: person.isTeammate, now };
  return personAddresses(person)
    .flatMap((email) => {
      const account =
        person.littlebirdAccounts.find((row) => row.email === email) ?? null;
      const state = stateOf(email, account, context);
      return state === null ? [] : { email, account, state };
    })
    .sort((a, b) => importanceOf(a) - importanceOf(b));
};

/** How the button of a person works */
type ButtonLayout = {
  /** The address that the main part of the button acts on: the one whose state matters most */
  main: AddressEntry;
  /** A chevron beside the main part opens the list of the addresses */
  hasAddressList: boolean;
  /** Decline answers the request that the main part would accept */
  hasDecline: boolean;
};

/** The button for the addresses of a person, or null when none has an action. With several addresses the main part
 * acts on the top one, and the rest are in the list. A request that came in takes over: the user answers it with
 * Accept or Decline, and the other addresses wait until then. */
export const buttonLayout = (
  entries: readonly AddressEntry[]
): ButtonLayout | null => {
  const [main] = entries;
  if (!main) return null;
  const isRequest = main.state === 'accept';
  return {
    main,
    hasAddressList: entries.length > 1 && !isRequest,
    hasDecline: isRequest,
  };
};

/** An address of a person on the pending connections, with the id of its Littlebird account */
export type PendingEntry = {
  person: WikiPerson;
  entry: AddressEntry;
  accountId: string;
};

/** The entries in the given states on the pending pages. No lookup is made for these pages, so the entries are the
 * connections that the pages hold. */
export const pendingEntries = (
  people: WikiPerson[],
  states: readonly AddressState[]
): PendingEntry[] =>
  people.flatMap((person) =>
    connectionActions(person, null).flatMap((entry) =>
      entry.account && states.includes(entry.state)
        ? { person, entry, accountId: entry.account.accountId }
        : []
    )
  );
